import React, { useEffect, useState } from 'react';
import { getDeployedSurveys, getSurveyTokens, getSystemSettings } from '../../api/adminApi';
import { sendSurvey } from '../../api/adminApi';

const DeployedSurveys = ({ user }) => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [viewYear, setViewYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [academicYearStartDate, setAcademicYearStartDate] = useState({ month: 9, day: 22 });
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [surveyLinkExpiryDays, setSurveyLinkExpiryDays] = useState(30);

  const formatDateDDMMYYYY = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getSurveyLinksExpiryDate = (survey) => {
    if (!survey || !survey.deployment_date) return null;
    if (!surveyLinkExpiryDays || surveyLinkExpiryDays <= 0) return null;

    const deploymentDate = new Date(survey.deployment_date);
    if (Number.isNaN(deploymentDate.getTime())) return null;

    const expiresAt = new Date(deploymentDate.getTime() + (surveyLinkExpiryDays * 24 * 60 * 60 * 1000));
    return Number.isNaN(expiresAt.getTime()) ? null : expiresAt;
  };

  const getCurrentAcademicYearStart = (startMonth, startDay, referenceDate = new Date()) => {
    const currentYear = referenceDate.getFullYear();
    const currentMonth = referenceDate.getMonth() + 1;
    const currentDay = referenceDate.getDate();

    if (currentMonth < startMonth || (currentMonth === startMonth && currentDay < startDay)) {
      return currentYear - 1;
    }

    return currentYear;
  };

  const getSurveyYear = (survey) => {
    if (survey && survey.survey_year) return Number(survey.survey_year);
    if (survey && survey.deployment_date) {
      const d = new Date(survey.deployment_date);
      if (!Number.isNaN(d.getTime())) return d.getFullYear();
    }
    return null;
  };

  const isTokenExpired = (token) => {
    if (!surveyLinkExpiryDays || surveyLinkExpiryDays <= 0) return false;
    if (!token || !token.sent_at) return false;
    const sentAt = new Date(token.sent_at);
    if (Number.isNaN(sentAt.getTime())) return false;
    const expiresAtMs = sentAt.getTime() + (surveyLinkExpiryDays * 24 * 60 * 60 * 1000);
    return Date.now() > expiresAtMs;
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await getDeployedSurveys();
      setSurveys(res || []);

      const years = Array.from(new Set((res || []).map(getSurveyYear).filter(y => !!y))).sort((a, b) => b - a);
      setAvailableYears(years);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getSystemSettings();
        if (settings && settings.academic_year_start_date) {
          const [m, d] = settings.academic_year_start_date.split('-').map(Number);
          if (Number.isInteger(m) && Number.isInteger(d)) {
            setAcademicYearStartDate({ month: m, day: d });
            setCurrentAcademicYear(getCurrentAcademicYearStart(m, d));
          }
        }

        const expiryDaysRaw = parseInt(settings?.survey_link_expiry_days, 10);
        if (!Number.isNaN(expiryDaysRaw)) {
          setSurveyLinkExpiryDays(expiryDaysRaw);
        }
      } catch (e) {
        console.error('Failed to load settings:', e);
        setCurrentAcademicYear(getCurrentAcademicYearStart(academicYearStartDate.month, academicYearStartDate.day));
      }
    };

    loadSettings();
    fetch();
  }, []);

  useEffect(() => {
    if (!viewYear && currentAcademicYear) {
      setViewYear(currentAcademicYear);
    }
  }, [availableYears, currentAcademicYear, viewYear]);

  useEffect(() => {
    setSelected(null);
    setTokens([]);
  }, [viewYear]);

  const openTokens = async (surveyId) => {
    setSelected(surveyId);
    try {
      const res = await getSurveyTokens(surveyId);
      setTokens(res);
    } catch (e) {
      console.error(e);
      setTokens([]);
    }
  };

  const handleGenerateLinks = async (surveyId) => {
    try {
      setLoading(true);
      const res = await sendSurvey(surveyId, false);
      // If API returns tokens, show them, otherwise fetch tokens list
      if (res && res.tokens) {
        setTokens(res.tokens.map(t => ({
          enrollment_id: t.enrollment_id,
          student_email: t.student_email,
          student_identifier: '',
          url: t.url,
          responded: false,
          responded_at: null,
          sent_at: null,
        })));
      } else {
        const refreshed = await getSurveyTokens(surveyId);
        setTokens(refreshed);
      }
      setSelected(surveyId);
    } catch (err) {
      console.error('Send failed', err);
      alert('Failed to generate/send links: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const filteredSurveys = (surveys || []).filter((s) => {
    if (!viewYear) return true;
    return getSurveyYear(s) === viewYear;
  });

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-indigo-700">Deployed Surveys</h2>
      <div className="mb-4 flex items-center gap-3">
        <div className="text-sm text-gray-700">
          Current academic year: <span className="font-semibold">{currentAcademicYear ? `${currentAcademicYear}-${currentAcademicYear + 1}` : 'Loading...'}</span>
        </div>
        <label className="font-semibold text-gray-700" htmlFor="deployed-year">View year:</label>
        <select
          id="deployed-year"
          value={viewYear || ''}
          onChange={(e) => setViewYear(e.target.value ? Number(e.target.value) : null)}
          className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          {availableYears.length === 0 ? <option value="">No deployed years</option> : null}
          {availableYears.map(year => (
            <option key={year} value={year}>{year}-{year + 1}</option>
          ))}
        </select>
      </div>
      {loading && <div>Loading...</div>}
      <div className="space-y-4">
        {filteredSurveys.map(s => {
          const displayYear = s.survey_year || (s.deployment_date ? new Date(s.deployment_date).getFullYear() : '');
          const displayTitle = `${s.module_name} - ${displayYear}`;
          return (
            <div key={s.survey_id} className="p-3 border rounded flex justify-between items-center">
              <div>
                <div className="font-semibold">{displayTitle}</div>
                <div className="text-sm text-gray-600">Module: {s.module_name} | Deployment: {s.deployment_date}</div>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => openTokens(s.survey_id)} className="px-3 py-2 bg-indigo-600 text-white rounded">View Links</button>
                <button onClick={() => handleGenerateLinks(s.survey_id)} className="px-3 py-2 bg-green-600 text-white rounded">Regenerate Links</button>
              </div>
            </div>
          );
        })}
        {filteredSurveys.length === 0 && !loading && <div className="text-sm text-gray-600">No deployed surveys found for this year.</div>}
      </div>

      {selected && (() => {
        const sel = surveys.find(x => x.survey_id === selected);
        const selYear = sel ? (sel.survey_year || (sel.deployment_date ? new Date(sel.deployment_date).getFullYear() : '')) : '';
        const selTitle = sel ? `${sel.module_name} - ${selYear}` : `Survey ${selected}`;
        const completedCount = tokens.filter(t => !!t.responded).length;
        const totalCount = tokens.length;
        const expiryDate = getSurveyLinksExpiryDate(sel);
        const linksHaveExpired = expiryDate ? (Date.now() > expiryDate.getTime()) : false;
        const expiryDateLabel = expiryDate ? formatDateDDMMYYYY(expiryDate) : '';
        return (
          <div className="mt-6">
            <h3 className="text-lg font-semibold">Links for {selTitle}</h3>
            <div className="mt-1 text-sm text-gray-700">
              Completed: <span className="font-medium">{completedCount}</span> / {totalCount}
            </div>
            {expiryDateLabel ? (
              <div className={`mt-1 text-sm ${linksHaveExpired ? 'text-red-700' : 'text-gray-700'}`}>
                {linksHaveExpired ? 'Links expired on ' : 'Links expire on '}
                <span className="font-medium">{expiryDateLabel}</span>
              </div>
            ) : null}
            <div className="mt-2 space-y-2 max-h-64 overflow-auto">
              {tokens.map(t => (
                <div key={t.enrollment_id} className={`p-2 border rounded flex justify-between items-center ${!t.responded && isTokenExpired(t) ? 'border-red-300 bg-red-50' : ''}`}>
                  <div className="text-sm">
                    <div className="font-medium">{t.student_email}</div>
                    <div className="text-xs text-gray-600">{t.student_identifier || ''}</div>
                    <div className="mt-1">
                      {t.responded ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Completed
                        </span>
                      ) : isTokenExpired(t) ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Expired
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Not completed
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    <a href={t.url} target="_blank" rel="noreferrer" className="text-indigo-600 underline">Open</a>
                  </div>
                </div>
              ))}
              {tokens.length === 0 && <div className="text-sm text-gray-600">No links available.</div>}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DeployedSurveys;
